import { IkeToken } from '@ikegps/core-api';
import { computed, makeObservable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { CheckBox, type CheckBoxState } from '../../components/input/input.checkbox';
import { IMithrilComponent } from '../../components/mithril.component';
import { Sortable } from '../../components/sortable/sortable';
import { Spinner } from '../../components/spinner/spinner';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { AppStore } from '../../store';
import { CollectionEditMenu } from '../collection.edit.menu/collection.edit.menu';
import { ExportMenu } from '../export.menu/export.menu';
import { TopologicalEditorCollectionButton } from '../topological.editor.button/topological.editor.button';
import { CSS, Style } from './collection.toolbar.style';

addStyles(Style);

export interface CollectionToolbarCfg {
	mapZoomOut?: boolean;
	title?: T;
}

export class CollectionToolbar extends IMithrilComponent {
	private state: TagState;
	private exportMenu: ExportMenu;
	private editMenu: CollectionEditMenu;
	private sortable: Sortable;

	private mapZoomOut: boolean;
	private defaultTitle: T;

	@computed get title(): string {
		const tag = AppStore.Tag.get(CurrentState.inspectedTagId);
		if (tag == null) {
			return Translate(this.defaultTitle);
		}

		if (tag.name != null) {
			return tag.name;
		}

		return Translate(T.COLLECTION_MISSING_TITLE);
	}

	constructor(state: TagState, cfg: CollectionToolbarCfg = {}) {
		super();
		makeObservable(this);

		this.state = state;
		this.editMenu = new CollectionEditMenu();
		this.exportMenu = new ExportMenu();
		this.sortable = new Sortable(TagSortState);

		this.mapZoomOut = cfg.mapZoomOut;
		this.defaultTitle = cfg.title;
	}

	get nodes(): MithrilVNode {
		const showMapModeButton =
			this.mapZoomOut === false &&
			Services.Token.has(IkeToken.FEATURE_MAP_MODE) &&
			(Services.UserProfile.get(Services.User.current.objectId)
				? !Services.UserProfile.isReadOnly(Services.UserProfile.get(Services.User.current.objectId))
				: false);

		if (CurrentState.Tag.tags.length === 0) {
			const title = this.title;
			const selectedUuid = CurrentState.inspectedCollectionRevision
				? AppStore.CollectionSummary.get(CurrentState.inspectedCollectionRevision).uuid
				: null;
			return m('div', { key: 'selection-toolbar', className: CSS.container }, [
				m('div', { className: CSS.title, title }, title),
				showMapModeButton ? TopologicalEditorCollectionButton.view(CurrentState.inspectedTagId, selectedUuid) : null,
				m('div', { className: CSS.tools }, [
					this.sortable.view(),
					this.mapZoomOut === false
						? null
						: ImageButton.view(MaterialIcon.ZOOM_OUT_MAP, {
								onclick: this.state.map.centerMap,
								title: T.TAG_ZOOM,
							}),
				]),
			]);
		}

		const checkboxState: CheckBoxState = {
			isSelected: () => this.state.allSelected,
			setSelected: (val: boolean) => CurrentState.Tag.selectAll(this.state.filteredTags, val),
		};

		return m('div', { key: 'selection-toolbar', className: CSS.container }, [
			CheckBox.view(checkboxState),
			m('div', { className: CSS.text }, Translate(T.TAG_TITLE_SELECTION, CurrentState.Tag.collectionSize)),
			this.viewSelectedTools(),
		]);
	}

	private viewSelectedTools() {
		if (!CurrentState.Tag.ready) {
			return Spinner.view();
		}

		return m('div', { className: CSS.tools }, [this.exportMenu.view(), this.editMenu.view()]);
	}
}
